import classNames from "classnames";
import i18next from "i18next";
import { html } from "lit-html";
import { PERUVIAN_CULTURE_CODE } from "Services/constants";
import { createAmericanAirlinesLoginUrl } from "Services/urls";
import { useLoginWidgetButton } from "./useLoginWidgetButton";
import { useState } from "Shared/haunted/CustomHooks";
import { DynamicSettings } from "Components/admin/DynamicSettings";

export interface Props {
	absoluteAction: string;
	bookingUrl: string;
	culture: string;
	dynamicSettings: DynamicSettings;
}

export const useLoginWidget = (props: Props) => {
	const [isOpen, setIsOpen] = useState(false);

	const [language] = props.culture.toLowerCase().split("-");

	const handlePersonClick = () => {
		const loginUrl = `${props.bookingUrl}/V2/Login?culture=${props.culture}&url=${props.absoluteAction}`;
		window.location.href = loginUrl;
	};

	const handleAmericanClick = () => (window.location.href = createAmericanAirlinesLoginUrl(props.culture));

	const handlePeruCompraClick = () => {
		const peruCompraLoginUrl = `${props.bookingUrl}/V2/Login?pcompra=1&culture=${props.culture}&url=${props.absoluteAction}`;
		window.location.href = peruCompraLoginUrl;
	};

	const handleAgencyClick = () => {
		const agencyLoginUrl = `${props.bookingUrl}/V2/Login?agency=1&culture=${props.culture}&url=${props.absoluteAction}`;
		window.location.href = agencyLoginUrl;
	};

	const handleCompanyClick = () => {
		const companyLoginUrl = `${props.bookingUrl}/V2/Login?company=1&culture=${props.culture}&url=${props.absoluteAction}`;
		window.location.href = companyLoginUrl;
	};

	const handleRegisterClick = () => {
		const companyLoginUrl = `${props.bookingUrl}/Member/Register?culture=${props.culture}&url=${props.absoluteAction}`;
		window.location.href = companyLoginUrl;
	};

	const person = useLoginWidgetButton({
		content: html` <i class="jsh-icon jsh-person mb-2 text-[40px]"></i> ${i18next.t("Personas")} `,
		onClick: handlePersonClick,
	});

	const american = useLoginWidgetButton({
		content: html`
			<div class="flex h-10 items-center justify-center">
				<img class="mb-2 h-auto w-25 max-w-[25vw]" src="/images/american-airlines/aa-logo.png" />
			</div>
			${i18next.t("AAdvantage® Program")}
		`,
		onClick: handleAmericanClick,
	});

	const peruCompra = useLoginWidgetButton({
		content: html`
			<img class="mb-2 h-10" src="/images/peru-compras/pcra-logo-black.svg" /> ${i18next.t("Peru Compras")}
		`,
		onClick: handlePeruCompraClick,
	});

	const agency = useLoginWidgetButton({
		content: html`
			<img class="w-20 sm:w-24" src="/images/cug2/cug-logo-agency-${language.trim().toLowerCase()}.svg" />
		`,
		fitHeight: true,
		onClick: handleAgencyClick,
	});

	const company = useLoginWidgetButton({
		content: html`
			<img class="w-20 sm:w-24" src="/images/cug2/cug-logo-company-${language.trim().toLowerCase()}.svg" />
		`,
		fitHeight: true,
		onClick: handleCompanyClick,
	});

	const separatorTemplate = () => html`<div class="border-b border-solid border-be-gray-9"></div>`;

	const closeButtonTemplate = () => html`
		<div
			class="absolute right-3 top-3 z-10 flex h-6 w-6 cursor-pointer items-center justify-center rounded-full bg-gray-500 font-semibold leading-none text-white hover:bg-gray-600"
			@click=${() => setIsOpen(false)}
		>
			&times;
		</div>
	`;

	const personalLoginOptionsTemplate = () => html`
		<div class=${classNames("justify-center py-5 flex gap-4", "sm:justify-between")}>
			${person.htmlTemplate()}
			${props.culture === PERUVIAN_CULTURE_CODE && props.dynamicSettings.IsPeruCompraOn
				? peruCompra.htmlTemplate()
				: ""}
			${props.dynamicSettings?.IsAmericanOn ? american.htmlTemplate() : ""}
		</div>
	`;

	const cugLoginOptionsTemplate = () => html`
		<div class="flex items-center justify-between gap-4 pb-1 pt-5 sm:pb-3">
			${agency.htmlTemplate()} ${company.htmlTemplate()}
		</div>
	`;

	const registerButtonTemplate = () => html`
		<div class="mt-5 flex justify-center">
			<div
				class="w-[75%] cursor-pointer rounded-lg border border-solid border-be-gray-9 py-3 text-center text-n-blue hover:bg-gray-100"
				@click=${handleRegisterClick}
			>
				${i18next.t("Inscríbete aquí")}
			</div>
		</div>
	`;

	const htmlTemplate = () => html`
		<div
			class=${classNames(
				"fixed right-0 top-0 w-full h-full z-[111111] flex transform flex-col items-center gap-4 overflow-y-auto",
				"bg-white text-n-blue text-left",
				"transition-transform duration-300 ease-in-out",
				"md:absolute md:top-[59px] lg:top-[90px] md:w-160 md:h-auto md:overflow-hidden md:border md:border-solid md:border-be-gray-9",
				"md:common-transition",
				{
					"translate-x-0": isOpen,
					"translate-x-full": !isOpen,
					"md:my-0 md:left-auto md:right-0": isOpen,
					"md:-right-160 md:m-0 md:translate-x-0": !isOpen,
				}
			)}
		>
			<div class=${classNames("relative p-4 w-full", "sm:px-8 sm:py-4")}>
				${closeButtonTemplate()}
				<div class=${classNames("text-lg mb-4 flex w-full font-extrabold text-n-blue", "sm:text-xl")}>
					${i18next.t("Log in with your account as")}:
				</div>
				${separatorTemplate()} ${personalLoginOptionsTemplate()}
				<div class="text-l mb-4 flex w-full text-left font-extrabold text-n-blue sm:text-xl">
					${i18next.t("Or you can log in as a company or an agency")}:
				</div>
				${separatorTemplate()} ${cugLoginOptionsTemplate()}
				<div class=${classNames("text-lg mb-4 font-extrabold text-n-blue", "sm:text-xl")}>
					${i18next.t("¿No tienes una cuenta?")}
				</div>
				${separatorTemplate()} ${registerButtonTemplate()}
			</div>
		</div>
	`;

	return {
		isOpen,
		close: () => setIsOpen(false),
		htmlTemplate,
		registerAccountClick: handleRegisterClick,
		toggle: () => setIsOpen(!isOpen),
	};
};
